import type React from 'react'
import {
  AlertTriangle,
  BarChart3,
  Bell,
  BookOpen,
  Bot,
  Brain,
  Briefcase,
  ClipboardList,
  Database,
  Fingerprint,
  HardDrive,
  History,
  Layers,
  Package,
  Search,
  ScrollText,
  Server,
  Settings,
  Shield,
  SlidersHorizontal,
  Target,
  Users,
  Globe,
  LayoutGrid,
} from 'lucide-react'
import type { NavigationPermissions, ResourceCRUD, ResourcePermissions } from '@/shared/auth/permission-context'
import { intelligenceFeatures } from '@/shared/config/intelligence-features'
import { getRuntimeEnv } from '@/shared/config/runtime-env'

export interface NavItem {
  nameKey: string
  href?: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number
  children?: NavItem[]
  permission?: keyof NavigationPermissions
  requiresWriteAccess?: boolean
  /**
   * Optional org-role allowlist. When present, the item is hidden from any user
   * whose role is not in the list, regardless of feature `permission`. Used to
   * restrict sensitive surfaces (e.g. entity resolution, which exposes/merges
   * cross-source PII) to owner/admin only.
   */
  requiredRoles?: string[]
  /**
   * Optional runtime feature gate. When present and it returns false the item
   * is hidden from the sidebar. Evaluated at render time (not module load) so a
   * runtime ConfigMap flag change is honored without a rebuild. Used by SP2 C3
   * to drop the Resolution entry on a reboot host at cutover (the page is served
   * by the retired legacy intelligence-zone).
   */
  featureFlag?: () => boolean
  /**
   * The resource action the destination exists to take, read from the
   * server's permission answer (resources.<resource>.<action> of
   * GET /users/me/permissions). The next sidebar hides the item unless the
   * answer allows it, and while the answer loads. The current sidebar does not
   * read it.
   */
  requiredAction?: { resource: keyof ResourcePermissions; action: keyof ResourceCRUD }
  /**
   * Presentation grouping inside a nav group. `advanced` marks a specialist
   * surface: it stays discoverable, rendered after the group's core entries
   * under an "Advanced" label. This is not an access control; role and flag
   * gates stay on `requiredRoles` / `featureFlag`.
   */
  section?: 'advanced'
}

/**
 * Splits a group's visible children into core entries and specialist
 * (`section: 'advanced'`) entries, each in declaration order. Both sidebars
 * render core first, then the Advanced label, then the advanced entries.
 */
export function partitionNavChildren(children: NavItem[]): { core: NavItem[]; advanced: NavItem[] } {
  return {
    core: children.filter((child) => child.section !== 'advanced'),
    advanced: children.filter((child) => child.section === 'advanced'),
  }
}

export const navigation: NavItem[] = [
  {
    nameKey: 'missions',
    icon: Target,
    permission: 'missions',
    children: [
      { nameKey: 'missionStudio', href: '/missions', icon: Layers, permission: 'missions' },
      { nameKey: 'missionGallery', href: '/missions/gallery', icon: LayoutGrid, permission: 'missions', featureFlag: () => getRuntimeEnv('VITE_MISSION_GALLERY_ENABLED') === 'true' },
      { nameKey: 'missionDesigner', href: '/missions/designer', icon: Briefcase, permission: 'missions' },
    ],
  },
  {
    nameKey: 'agents',
    icon: Bot,
    permission: 'agents',
    children: [
      { nameKey: 'agentOverview', href: '/agents', icon: Bot, permission: 'agents' },
    ],
  },
  {
    nameKey: 'governance',
    icon: Shield,
    permission: 'governance',
    children: [
      { nameKey: 'policyCenter', href: '/governance', icon: ScrollText, permission: 'governance' },
      { nameKey: 'trustManagement', href: '/governance/trust', icon: Shield, permission: 'governance' },
      { nameKey: 'resources', href: '/governance/resources', icon: Database, permission: 'resources' },
      { nameKey: 'escalations', href: '/governance/escalations', icon: AlertTriangle, permission: 'governance' },
    ],
  },
  {
    nameKey: 'infrastructure',
    icon: HardDrive,
    permission: 'agents',
    children: [
      { nameKey: 'infraOverview', href: '/infrastructure', icon: HardDrive, permission: 'agents' },
      { nameKey: 'hosts', href: '/infrastructure/hosts', icon: Server, permission: 'agents' },
      { nameKey: 'clusters', href: '/infrastructure/clusters', icon: Layers, permission: 'agents' },
    ],
  },
  {
    nameKey: 'intelligence',
    icon: Brain,
    permission: 'agents',
    children: [
      { nameKey: 'capsules', href: '/intelligence/capsules', icon: Package, permission: 'agents' },
      { nameKey: 'retrieval', href: '/intelligence/retrieval', icon: Search, permission: 'agents', featureFlag: intelligenceFeatures.retrievalTuning },
      // SP2 C3: Resolution is served by the legacy Rust intelligence-zone; hide
      // the entry on a reboot host at cutover (legacyZoneSurfaces flag off).
      { nameKey: 'resolution', href: '/intelligence/resolution', icon: Database, permission: 'agents', featureFlag: intelligenceFeatures.legacyZoneSurfaces },
      // ADR-006 WP-I: reboot Go-runtime identity workbench (replaces Resolution at cutover).
      { nameKey: 'identityWorkbench', href: '/intelligence/identity', icon: Fingerprint, permission: 'agents', requiredRoles: ['owner', 'admin'] },
      { nameKey: 'vocabulary', href: '/intelligence/vocabulary', icon: BookOpen, permission: 'agents' },
      // Specialist surface (raw extraction-model hyperparameters). Interim:
      // ADR-20260616 scopes customer self-service tuning as its own slice, so
      // this is "advanced until that surface ships", not specialist-only.
      { nameKey: 'fineTuning', href: '/intelligence/fine-tuning', icon: SlidersHorizontal, permission: 'agents', featureFlag: intelligenceFeatures.extractionCuration, section: 'advanced' },
    ],
  },
  {
    nameKey: 'platform',
    icon: BarChart3,
    children: [
      { nameKey: 'users', href: '/users', icon: Users, permission: 'members' },
      { nameKey: 'organization', href: '/organization', icon: Globe, permission: 'organization' },
      { nameKey: 'settings', href: '/settings', icon: Settings, permission: 'settings' },
      { nameKey: 'audit', href: '/audit', icon: History, permission: 'audit' },
      { nameKey: 'waitlist', href: '/waitlist', icon: ClipboardList, permission: 'waitlist' as keyof NavigationPermissions },
      { nameKey: 'requestedFeatures', href: '/requested-features', icon: Bell, permission: 'waitlist' as keyof NavigationPermissions },
    ],
  },
]
